import React, { useEffect, useState } from 'react';
import { Navigate, useLocation, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useBetaEnd } from '../context/BetaEndContext';
import MobileDesktopModal from './MobileDesktopModal';

const ProtectedRoute = ({ children, requireAdmin = false, requireSuperAdmin = false }) => {
  const { user, loading, isAdmin, isSuperAdmin, checkAuth } = useAuth();
  const location = useLocation();
  const params = useParams();
  const betaEnd = useBetaEnd();
  const [isChecking, setIsChecking] = useState(!user);

  useEffect(() => {
    // If user data passed from AuthCallback, skip auth check
    if (location.state?.user) {
      setIsChecking(false);
      return;
    }

    if (!user && !loading) {
      checkAuth().finally(() => setIsChecking(false));
    } else {
      setIsChecking(false);
    }
  }, [user, loading, checkAuth, location.state]);

  // Show loading while checking auth
  if (loading || isChecking) {
    return (
      <div className="min-h-screen bg-space-900 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 border-4 border-teal-400/30 border-t-teal-400 rounded-full animate-spin" />
          <p className="text-gray-400 text-sm">Loading...</p>
        </div>
      </div>
    );
  }

  // Not authenticated
  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Mandatory CC-on-file setup: if user has pending_stripe_setup, force them to /billing-setup
  // (Admins are exempt so they can still access the admin panel.)
  const onBillingSetup = location.pathname.startsWith('/billing-setup');
  if (user.pending_stripe_setup && !onBillingSetup && !isAdmin && !isSuperAdmin) {
    return <Navigate to="/billing-setup" replace />;
  }

  // Check admin requirements
  if (requireSuperAdmin && !isSuperAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  if (requireAdmin && !isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  // Beta-ended paused workspace — lock user to agency-level pages only.
  // Allowed: dashboard, sub-accounts list, settings, billing, admin.
  // Blocked: any /workspace/:ws/sub-account/:sa/... path.
  if (betaEnd?.isPaused && !isAdmin) {
    const path = location.pathname || '';
    const inSubAccount = /\/workspace\/[^/]+\/sub-account\//.test(path);
    if (inSubAccount) {
      // Extract workspace id from the URL so we redirect to its agency dashboard
      const m = path.match(/\/workspace\/([^/]+)\/sub-account\//);
      const wsId = m?.[1] || params?.workspaceId;
      return <Navigate to={wsId ? `/workspace/${wsId}/billing` : '/dashboard'} replace />;
    }
  }

  return (
    <>
      {children}
      <MobileDesktopModal />
    </>
  );
};

export default ProtectedRoute;
